import React, { useState } from "react";
import {
  MediaMarqueeBase,
  Content,
  Headline,
  Description,
  VideoWrapper,
  VideoEmbed,
  PrimaryCopy,
  PrimaryWrapper,
  Features,
  Feature,
  FeatureImage,
  FeatureTitle,
  FeatureDescription,
  Footer,
} from "./media-marquee.styled";
import { MediaMarqueeSliceFragment } from "../../types/graphql";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import { htmlSerializer } from "../../utils/htmlSerializer";
import Youtube from "../youtube/youtube";
import { youtubeRegEx } from "../../utils/constants";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";
import VisibilitySensor from "react-visibility-sensor";

export type IMediaMarqueeProps = Slice<
  SharedSliceType,
  MediaMarqueeSliceFragment["primary"],
  MediaMarqueeSliceFragment["items"][number]
> & { type: SharedSliceType };

const MediaMarquee = ({
  slice,
  context,
}: SliceComponentProps<IMediaMarqueeProps, IPageContext>) => {
  const features = slice.items;
  const video = slice.primary?.video_url;
  const youtubeRegexMatch = video?.embed_url?.match(youtubeRegEx);
  const [hasViewed, setHasViewed] = useState(false);

  return (
    (slice && (
      <MediaMarqueeBase className="slice-section">
        <VisibilitySensor
          onChange={(isVisible: boolean) => isVisible && setHasViewed(true)}
          offset={{ bottom: -1200 }}
        >
          <Content className="container">
            <PrimaryWrapper className="grid">
              <PrimaryCopy className="col-span-4 md-col-span-8 lg-col-span-5">
                {slice.primary?.headline && (
                  <header>
                    <Headline>{slice.primary?.headline}</Headline>
                  </header>
                )}
                {slice.primary?.media_marquee_description?.richText && (
                  <Description>
                    <PrismicRichText
                      field={slice.primary?.media_marquee_description.richText}
                      components={htmlSerializer}
                    />
                  </Description>
                )}
              </PrimaryCopy>
              {video && (
                <VideoWrapper className="col-span-4 md-col-span-8 lg-col-span-7">
                  <VideoEmbed>
                    {(youtubeRegexMatch &&
                      youtubeRegexMatch.length > 0 &&
                      hasViewed && (
                        <Youtube
                          embedURL={video.embed_url || ""}
                          thumbnailURL={video.thumbnail_url || ""}
                          title={video.title || ""}
                        />
                      )) ||
                      (video.embed_url && hasViewed && (
                        <iframe
                          width="100%"
                          height="100%"
                          src={video.embed_url}
                          frameBorder="0"
                          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ))}
                  </VideoEmbed>
                </VideoWrapper>
              )}
            </PrimaryWrapper>
            {features && features.length > 0 && (
              <Features className="grid">
                {features.slice(0, 3).map(
                  (feature, index) =>
                    feature && (
                      <Feature
                        key={index}
                        className="col-span-4 md-col-span-8 lg-col-span-4"
                      >
                        {feature.feature_image?.url && (
                          <FeatureImage
                            src={feature.feature_image.url}
                            alt={feature.feature_image.alt || "Xometry Image"}
                            width={feature.feature_image.dimensions?.width}
                            height={feature.feature_image.dimensions?.height}
                          />
                        )}
                        {feature.feature_title && (
                          <FeatureTitle>{feature.feature_title}</FeatureTitle>
                        )}
                        {feature.feature_description && (
                          <FeatureDescription>
                            <PrismicRichText
                              field={feature.feature_description.richText}
                              components={htmlSerializer}
                            />
                          </FeatureDescription>
                        )}
                      </Feature>
                    )
                )}
              </Features>
            )}
            {slice.primary?.footer?.richText &&
              slice.primary?.footer?.richText.length > 0 && (
                <Footer>
                  <PrismicRichText
                    field={slice.primary.footer.richText}
                    components={htmlSerializer}
                  />
                </Footer>
              )}
          </Content>
        </VisibilitySensor>
      </MediaMarqueeBase>
    )) || <></>
  );
};

export default MediaMarquee;
